import React from 'react';
import { useDispatch } from 'react-redux';
import { Badge, styled } from '@mui/material';
import { GridActionsCellItem, type GridColDef } from '@mui/x-data-grid-premium';

import { useSettings, Icon } from 'app/main/components';
import { ViewTableUtils, ViewTable } from 'app/main/components/page-layouts';
import { showInvoicesModuleEditDialog } from '../components/InvoicesModuleEditDialog';
import { showInvoicesDeadlinesEditNotesDialog } from '../components/InvoicesDeadlinesEditNotesDialog';

const StyledViewTable = styled(ViewTable)(({ theme }) => ({
	'& .InvoicesDeadlinesTab-ForcedAmount': {
		color: theme.palette.warning.main
	}
}));

export default function InvoicesDeadlinesTab() {
	const { t } = useSettings();
	const dispatch = useDispatch();

	const [refresh, setRefresh] = React.useState(false);
	const updateRefresh = () => setRefresh(state => !state);

	function editRow(row) {
		dispatch(showInvoicesModuleEditDialog({ invoiceUid: row.indl_invc_uid, updateRefresh }));
	}

	function editRowNotes(row) {
		dispatch(showInvoicesDeadlinesEditNotesDialog({ model: row, updateRefresh }));
	}

	const columns: GridColDef[] = [
		{
			field: 'invc_title',
			headerName: t('INVOICES_DEADLINES_COLUMN_INVOICE_TITLE'),
			minWidth: 150,
			renderCell: ViewTableUtils.renderCellWithTooltip,
			hideable: false
		},
		{
			field: 'comp_title',
			headerName: t('INVOICES_DEADLINES_COLUMN_COMPANY'),
			minWidth: 200,
			flex: 1,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({ value, to: { module: 'companies', uid: row.invc_comp_uid } }),
			hideable: true
		},
		{
			field: 'indl_d_deadline',
			headerName: t('INVOICES_DEADLINES_COLUMN_DATE_DEADLINE'),
			minWidth: 120,
			renderCell: ViewTableUtils.renderDate,
			hideable: false
		},
		{
			field: 'indl_d_payment',
			headerName: t('INVOICES_DEADLINES_COLUMN_DATE_PAYMENT'),
			minWidth: 120,
			renderCell: ViewTableUtils.renderDate,
			hideable: false
		},
		{
			field: 'indl_amount',
			type: 'number',
			headerName: t('INVOICES_DEADLINES_COLUMN_AMOUNT'),
			minWidth: 150,
			renderCell: ViewTableUtils.renderCurrency,
			cellClassName: ({ row }) =>
				row.inty_group === 'expenses' ? 'font-semibold text-red-600' : 'font-semibold text-green-700',
			hideable: false
		},
		{
			field: 'indl_amount_payment',
			type: 'number',
			headerName: t('INVOICES_DEADLINES_COLUMN_AMOUNT_PAYMENT'),
			minWidth: 150,
			cellClassName: ({ row }) => (row.indl_force_close ? 'InvoicesDeadlinesTab-ForcedAmount' : ''),
			renderCell: ViewTableUtils.renderCurrency,
			hideable: false
		},
		{
			field: 'pame_title',
			headerName: t('INVOICES_DEADLINES_COLUMN_PAYMENT_TYPE'),
			minWidth: 150,
			renderCell: ViewTableUtils.renderCellWithTooltip
		},
		{
			field: 'indl_force_close',
			headerName: t('INVOICES_DEADLINES_COLUMN_FORCE_CLOSE'),
			type: 'boolean',
			minWidth: 50
		},
		{
			field: 'indl_closed',
			headerName: t('INVOICES_DEADLINES_COLUMN_CLOSED'),
			type: 'boolean',
			minWidth: 50
		},
		{
			type: 'actions',
			field: 'actions',
			width: 80,
			renderCell: ({ row }) => [
				<GridActionsCellItem
					label={t('VIEW_ITEM_BUTTON_EDIT')}
					icon={<Icon icon="edit" />}
					onClick={() => editRow(row)}
				/>,
				<GridActionsCellItem
					label={t('DETAILS_INVOICES_DEADLINES_FIELD_NOTES')}
					icon={
						<Badge color="secondary" variant="dot" invisible={!row.indl_notes}>
							<Icon icon="comment-text-outline" />
						</Badge>
					}
					onClick={() => editRowNotes(row)}
				/>
			],
			sortable: false
		}
	];

	return (
		<StyledViewTable
			id="invoicesDeadlinesView"
			module="invoices_deadlines"
			columns={columns}
			refresh={refresh}
			updateRefresh={updateRefresh}
		/>
	);
}
